import type { ShellReloadReason } from '@abax/platform';
import { getShellBaseUrl } from '@/services/core';
import { runBeforeReloadHooks } from './before-reload-hooks';

export { registerBeforeReload } from './before-reload-hooks';

/**
 * How long the shell holds the page open for MFE hooks on a version update.
 *
 * Deliberately a constant and not an `env-config.json` key: the value trades a
 * user-visible delay against the amount of state an MFE can flush, which is a
 * product decision rather than something ops tune per environment.
 */
const VERSION_UPDATE_BUDGET_MS = 2000;

/**
 * SSOT for how long each teardown cause waits. Call sites pass a reason only, so
 * a new site cannot invent its own budget, and changing one is a single edit.
 *
 * Zero means notify-only: the hooks still fire, the shell does not wait. That is
 * every cause where waiting either harms the user (maintenance should cut over
 * now) or has nothing to preserve (access changed, the page they land on is a
 * different one).
 */
const BUDGET_MS: Record<ShellReloadReason, number> = {
  'new-version': VERSION_UPDATE_BUDGET_MS,
  'important-new-version': VERSION_UPDATE_BUDGET_MS,
  'manual-update': VERSION_UPDATE_BUDGET_MS,
  maintenance: 0,
  'access-revoked': 0,
  'access-granted': 0,
  'account-changed': 0,
  'stale-version-navigation': 0,
  retry: 0,
  'stale-asset': 0,
};

/**
 * The teardown a waited cause has queued behind the hook phase, if any. Holding
 * only the latest means a cause that arrives during the wait takes the
 * destination: access being revoked mid-countdown is more current than the
 * version update that started the wait, and dropping it would reload the user
 * onto the package they just lost.
 */
let pendingPerform: (() => void) | null = null;

/**
 * Runs the hook phase, then the actual teardown.
 *
 * On a zero budget nothing is awaited before `perform`, so those causes navigate
 * in the same tick and behave exactly as they did before hooks existed. That
 * also means they win outright over a waited teardown already in flight, which
 * is what stops a menu click looking dead for two seconds after a countdown
 * expires.
 *
 * Nothing here is a permanent latch. If a navigation fails to unload the
 * document, later teardowns still work.
 */
function teardown(reason: ShellReloadReason, perform: () => void): void {
  const budgetMs = BUDGET_MS[reason];
  const hooks = runBeforeReloadHooks(reason, budgetMs);

  if (budgetMs <= 0) {
    // This cause is now the most recent one, so a waited teardown still queued
    // behind the hook phase is stale: its destination predates this one.
    pendingPerform = null;
    perform();
    return;
  }

  const alreadyQueued = pendingPerform !== null;
  pendingPerform = perform;
  if (alreadyQueued) return;

  void hooks.then(() => {
    const run = pendingPerform;
    pendingPerform = null;
    run?.();
  });
}

/** Reload the current document. */
export function reloadPage(reason: ShellReloadReason): void {
  teardown(reason, () => window.location.reload());
}

/**
 * Leave the current document for `url`. Reloads instead when already there:
 * assigning the same href is a no-op in the browser, which would leave the shell
 * showing stale federated packages.
 */
export function hardNavigate(url: string, reason: ShellReloadReason): void {
  teardown(reason, () => {
    if (window.location.href === url) {
      window.location.reload();
      return;
    }
    window.location.href = url;
  });
}

/** Leave for the shell's base URL, the landing route after an access change. */
export function navigateToShellRoot(reason: ShellReloadReason): void {
  hardNavigate(getShellBaseUrl().href, reason);
}
